import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import { BLOG_POSTS } from '../constants';
import { AUTOREN } from '../components/blog/AuthorBio';
import { Container, ButtonLink } from '../components/site/ui';
import { ScrollProgress } from '../components/ui/scroll-progress';

import { ARTIKEL } from './blog/registry';
import { srcSet } from '../lib/bilder';

const BlogPost: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const Artikel = ARTIKEL[slug];
  if (!post || !Artikel) return <Navigate to="/blog" replace />;

  const i = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const neuer = i > 0 ? BLOG_POSTS[i - 1] : null;
  const aelter = i < BLOG_POSTS.length - 1 ? BLOG_POSTS[i + 1] : null;
  const autor = Object.values(AUTOREN).find((a) => a.name === post.author);

  return (
    <article>
      <ScrollProgress className="top-[64px] h-0.5 bg-linear-to-r from-primary via-sky-400 to-violet" />

      <header className="relative overflow-hidden">
        <div className="glow-top pointer-events-none absolute inset-0" aria-hidden="true" />
        <Container narrow className="relative pt-10 pb-10 md:pt-14">
          <nav aria-label="Brotkrumen" className="mb-8 flex flex-wrap items-center gap-1.5 font-mono text-xs text-muted">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="size-3.5" aria-hidden="true" />
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <ChevronRight className="size-3.5" aria-hidden="true" />
            <span className="line-clamp-1 text-ink" aria-current="page">{post.category}</span>
          </nav>
          <span className="inline-block rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-mono text-xs text-primary">{post.category}</span>
          <h1 className="mt-5 text-3xl font-semibold leading-[1.1] text-white md:text-5xl">{post.title}</h1>
          <div className="mt-7 flex items-center gap-3 text-sm text-muted">
            {autor && <img src={autor.portrait} alt="" width={44} height={44} className="size-11 rounded-full object-cover ring-2 ring-primary/30" />}
            <span>
              <span className="block font-medium text-white">{post.author}</span>
              <span>{post.date}{post.updated && <> · aktualisiert am {post.updated}</>}</span>
            </span>
          </div>
        </Container>
        <Container className="relative max-w-5xl pb-4">
          <img src={post.imageUrl} srcSet={srcSet(post.imageUrl)} sizes="(min-width: 1024px) 1024px, 100vw" alt={post.imageAlt ?? post.title} width={1200} height={630} loading="eager" {...{ fetchpriority: 'high' }}
               className="w-full rounded-2xl border border-line object-cover" />
        </Container>
      </header>

      <Container narrow className="py-12 md:py-16">
        <Artikel />
      </Container>

      <Container narrow className="pb-16">
        <div className="grid gap-4 border-t border-line pt-10 md:grid-cols-2">
          {aelter && (
            <Link to={`/blog/${aelter.slug}`} className="card card-hover group p-6">
              <span className="flex items-center gap-2 font-mono text-xs text-muted"><ArrowLeft className="size-3.5" aria-hidden="true" />Älterer Artikel</span>
              <span className="mt-3 line-clamp-2 block font-semibold text-white group-hover:text-primary">{aelter.title}</span>
            </Link>
          )}
          {neuer && (
            <Link to={`/blog/${neuer.slug}`} className="card card-hover group p-6 md:col-start-2 md:text-right">
              <span className="flex items-center gap-2 font-mono text-xs text-muted md:justify-end">Neuerer Artikel<ArrowRight className="size-3.5" aria-hidden="true" /></span>
              <span className="mt-3 line-clamp-2 block font-semibold text-white group-hover:text-primary">{neuer.title}</span>
            </Link>
          )}
        </div>
        <div className="mt-12 rounded-2xl border border-line-2 bg-surface/70 p-8 text-center">
          <h2 className="text-2xl font-semibold text-white">Lassen Sie uns sprechen</h2>
          <p className="mx-auto mt-3 max-w-lg text-muted">Ob Sie eine IT-Position besetzen oder Ihren nächsten Schritt planen: ein erstes Gespräch ist unverbindlich.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/kontakt?typ=unternehmen" arrow>Position besprechen</ButtonLink>
            <ButtonLink to="/kontakt?typ=kandidat" variant="secondary">Karrieregespräch</ButtonLink>
          </div>
        </div>
      </Container>
    </article>
  );
};

export default BlogPost;
